import { useState } from 'react';
import { toast } from 'sonner';
import {
  useCreateOrgConsultantHoursCheckoutSession,
  useGetOrgConsultantHourPackages,
} from '@/api/auth/billing/billing';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { DATA_MISSION_PRODUCT_ID } from '@/constants/products';
import { useError } from '@/contexts/ErrorContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate, formatHours, formatPackagePrice, getDateLocale } from '@/lib/support';

interface BuyHoursDialogProps {
  orgId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Lets an Administrator or Billing user pick a Data Wrangler hour package and continue to Stripe
 * checkout. Stripe returns to the current page, where the balance is re-fetched; the hours are
 * credited once the payment completes.
 */
export function BuyHoursDialog({ orgId, open, onOpenChange }: BuyHoursDialogProps) {
  const { t, language } = useLanguage();
  const locale = getDateLocale(language);
  const { handleApiError } = useError();
  const [packageId, setPackageId] = useState<string | null>(null);

  const { data, isLoading, error } = useGetOrgConsultantHourPackages(orgId, DATA_MISSION_PRODUCT_ID, {
    query: { enabled: open },
  });
  const packages = data?.data.packages ?? [];
  const selectedId = packageId ?? (packages.length > 0 ? packages[0].packageId : null);
  const checkout = useCreateOrgConsultantHoursCheckoutSession();

  /**
   * Starts Stripe checkout for the selected package and redirects to it.
   */
  const handleCheckout = async () => {
    if (!selectedId) return;
    try {
      const response = await checkout.mutateAsync({
        orgId,
        data: {
          packageId: selectedId,
          successUrl: window.location.href,
          cancelUrl: window.location.href,
          locale: language,
        },
      });
      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (err) {
      handleApiError(err, { context: 'BuyHoursDialog.handleCheckout' }, { showToast: false });
      toast.error(t.support.buyHours.checkoutFailed);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.support.buyHours.title}</DialogTitle>
          <DialogDescription>{t.support.buyHours.description}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t.support.buyHours.loading}</p>
        ) : error ? (
          <p className="text-sm text-destructive">{t.support.buyHours.loadFailed}</p>
        ) : packages.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.support.buyHours.empty}</p>
        ) : (
          <RadioGroup value={selectedId ?? undefined} onValueChange={setPackageId} className="gap-2">
            {packages.map((offer) => (
              <Label
                key={offer.packageId}
                htmlFor={`hour-package-${offer.packageId}`}
                className="flex cursor-pointer items-center gap-3 rounded-md border p-3 font-normal has-[[data-state=checked]]:border-primary"
              >
                <RadioGroupItem id={`hour-package-${offer.packageId}`} value={offer.packageId} />
                <span className="flex-1">
                  <span className="block font-medium">{offer.displayName}</span>
                  <span className="block text-xs text-muted-foreground">
                    {fillSupportTemplate(t.support.buyHours.packageHours, { hours: formatHours(offer.hours, locale) })}
                  </span>
                </span>
                <span className="text-sm font-medium">
                  {formatPackagePrice(offer.unitAmount ?? undefined, offer.currency, locale)}
                </span>
              </Label>
            ))}
          </RadioGroup>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            {t.support.buyHours.cancel}
          </Button>
          <Button disabled={!selectedId || checkout.isPending} onClick={() => { void handleCheckout(); }}>
            {checkout.isPending ? t.support.buyHours.redirecting : t.support.buyHours.checkout}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
